import { assets, dialogs, utils, view } from '@yola/ws-sdk';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import segment from 'src/js/modules/analytics/segment';
import getDefaultTraits from 'src/js/modules/analytics/segment/helpers/get-default-traits';
import UploadImageTrigger from 'src/js/modules/common/components/upload-image-trigger';
import UploadImageContainer from 'src/js/modules/common/containers/upload-image-container';
import imageTypes from 'src/js/modules/extensions/common/constants/image-types';
import mediaAttributes from 'src/js/modules/extensions/common/constants/media-attributes';
import focalPoint from 'src/js/modules/focal-point';
import getImageTypeTrait from 'src/js/modules/focal-point/helpers/get-image-type-trait';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import getBreakpointForCurrentDevice from 'src/js/modules/breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_BREAKPOINT } from 'src/js/modules/breakpoint-editing/constants/breakpoints';
import getOverlappedMediaContainerByText from '../../helpers/get-overlapped-media-container-by-text';
import isPlaceholder from '../../helpers/is-placeholder';
import isBusinessLogo from '../../helpers/is-business-logo';
import getRatio from '../../helpers/get-ratio';

const {
  constants: { common: focalPointConstants, operationTypes },
  helpers: { isGalleryItem: isGalleryItemVerifier, getViewOperation, computeAdjustedScale },
} = focalPoint;
const {
  track,
  constants: { events },
} = segment;

const { DEFAULT_CONTENT_POSITION_STRING_VALUE, DEFAULT_SCALE, FILE_UPLOAD, STOCK_PHOTOS } =
  focalPointConstants;

function UploadImageTriggerContainer({
  getContext = utils.noop,
  elementId: liveElementId = null,
  id,
  active = false,
}) {
  const liveElement = view.accessors.getLiveElement(liveElementId);
  const overlappedMediaContainer = getOverlappedMediaContainerByText(liveElement, true);
  const elementId = overlappedMediaContainer
    ? view.accessors.getLiveElementId(overlappedMediaContainer)
    : liveElementId;

  const imageNode = useMemo(() => view.accessors.getLiveElement(elementId), [elementId]);

  const isGalleryItem = useMemo(() => isGalleryItemVerifier(imageNode), [imageNode]);
  const isLogo = useMemo(() => isBusinessLogo(imageNode), [imageNode]);

  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;
  const activeBreakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice()
    : DESKTOP_BREAKPOINT;

  const finishImageUpload = useCallback(
    async ({ source: assetSource, isStockPhoto }) => {
      const getOperation = (operationType, value) =>
        getViewOperation(operationType, elementId, value, activeBreakpoint);

      track(events.CONTROL_PANE_NEW_IMAGE_SELECTED, {
        ...getDefaultTraits(elementId),
        source: isStockPhoto ? STOCK_PHOTOS : FILE_UPLOAD,
      });

      const imageUrl = assets.helpers.addBaseHref(assetSource);
      const imageSize = await assets.helpers.getImageSize(imageUrl);

      const setSrcOperation = getOperation(operationTypes.SRC, assetSource);
      const setPositionOperation = getOperation(
        operationTypes.POSITION,
        DEFAULT_CONTENT_POSITION_STRING_VALUE
      );

      // adjusting content scale so small images want be stretching to container width or height
      const element = view.accessors.getLiveElement(elementId);
      const adjustedScale = computeAdjustedScale(element, {
        originalMediaBounds: imageSize,
        scale: DEFAULT_SCALE,
      });

      const scale = isLogo ? DEFAULT_SCALE : adjustedScale || DEFAULT_SCALE;
      const setScaleOperation = getOperation(operationTypes.SCALE, scale);

      let operations = [setSrcOperation, setPositionOperation, setScaleOperation];

      if (isLogo && imageSize.width && imageSize.height) {
        const setAspectRatioOperation = getOperation(
          operationTypes.ASPECT_RATIO,
          getRatio(imageSize)
        );

        operations = [...operations, setAspectRatioOperation];
      }

      if (isGalleryItem) {
        const setOriginSrcOperation = getOperation(operationTypes.ORIGIN_SRC, assetSource);
        const setOriginWidthOperation = getOperation(operationTypes.ORIGIN_WIDTH, imageSize.width);
        const setOriginHeightOperation = getOperation(
          operationTypes.ORIGIN_HEIGHT,
          imageSize.height
        );

        operations = [
          ...operations,
          setOriginSrcOperation,
          setOriginWidthOperation,
          setOriginHeightOperation,
        ];
      }

      if (isPlaceholder(imageNode)) {
        operations.push([
          view.operations.removeElementAttribute,
          [elementId, mediaAttributes.PLACEHOLDER_ATTRIBUTE],
        ]);
      }

      view.operations.bulkViewOperations(operations);

      dialogs.operations.hide();
    },
    [activeBreakpoint, elementId, imageNode, isGalleryItem, isLogo]
  );

  return (
    <UploadImageContainer onImageUploadEnd={finishImageUpload}>
      {({ onWrongFileType, onImageUpload, onStockPhotoUpload }) => (
        <UploadImageTrigger
          id={id}
          active={active}
          elementId={elementId}
          getContext={getContext}
          accept={imageTypes.IMAGE_ACCEPT_TYPE}
          onWrongFileType={onWrongFileType}
          onPhotoStockDialogCancel={dialogs.operations.hide}
          onClick={() => {
            track(events.CONTROL_PANE_REPLACE_MEDIA_BUTTON_CLICKED, {
              ...getDefaultTraits(elementId),
              imageType: getImageTypeTrait(elementId),
            });
          }}
          onTriggerUploadImageClick={() => {
            track(events.CONTROL_PANE_UPLOAD_FILE_BUTTON_CLICKED, {
              ...getDefaultTraits(elementId),
            });
          }}
          onTriggerBrowseStockPhotosClick={() => {
            track(events.CONTROL_PANE_BROWSE_STOCK_PHOTOS_BUTTON_CLICKED, {
              ...getDefaultTraits(elementId),
            });
          }}
          onImageUpload={onImageUpload}
          onStockPhotoUpload={onStockPhotoUpload}
        />
      )}
    </UploadImageContainer>
  );
}

UploadImageTriggerContainer.propTypes = {
  id: PropTypes.string.isRequired,
  elementId: PropTypes.string,
  getContext: PropTypes.func,
  active: PropTypes.bool,
};

export default UploadImageTriggerContainer;
